import { $axiosManager } from '@/api/$axios';
import { AUTH_REQUEST_TIMEOUT_MS } from '@/constants/auth';
import { getAttributionForRequest, getStoredAttribution } from '@/helpers/attribution';

// Thin API layer for backend-owned auth. $axiosManager auto-converts
// camelCase <-> snake_case, so request/response bodies stay camelCase here.

// Register sends the stored first touch when present, capturing the lead's acquisition source
// even if verification is never completed. Confirm and OAuth add current browser context below.
const attributionField = () => {
  const attribution = getStoredAttribution();
  return attribution ? { attribution } : {};
};

export async function register({ email, password, firstName, lastName, phone, referralCode }) {
  const { data } = await $axiosManager.post('/auth/register', {
    email,
    password,
    firstName,
    lastName,
    phone,
    referralCode,
    ...attributionField(),
  });
  return data;
}

export async function confirm({ email, code }) {
  const { data } = await $axiosManager.post('/auth/confirm', {
    email,
    code,
    attribution: getAttributionForRequest(),
  });
  return data;
}

export async function resendCode(email) {
  await $axiosManager.post('/auth/resend-code', { email });
}

export async function login({ email, password }) {
  const { data } = await $axiosManager.post('/auth/login', { email, password });
  return data;
}

export async function forgotPassword(email) {
  const { data } = await $axiosManager.post('/auth/forgot-password', { email });
  return data;
}

export async function resetPassword({ email, code, newPassword }) {
  await $axiosManager.post('/auth/reset-password', { email, code, newPassword });
}

export async function me(config) {
  const { data } = await $axiosManager.get('/users/me', config);
  return data;
}

export async function updateProfile({ firstName, lastName, phone }) {
  const { data } = await $axiosManager.patch('/users/me', { firstName, lastName, phone });
  return data;
}

export async function logout(accessToken, refreshToken = null) {
  // Explicit token: session.logout clears local auth state before this call, so the request
  // interceptor can no longer resolve a session token (and must not try to refresh one mid-logout).
  // The refresh token lets the backend revoke this device's server-side session.
  // A forced logout can be awaited by a refresh 401 handler while the session lock is held.
  const config = { timeout: AUTH_REQUEST_TIMEOUT_MS };
  if (accessToken) config.headers = { Authorization: `Bearer ${accessToken}` };
  const { data } = await $axiosManager.post('/auth/logout', { refreshToken }, config);
  return data;
}

export async function logoutAll() {
  await $axiosManager.post('/auth/logout-all');
}

export async function listSessions() {
  const { data } = await $axiosManager.get('/auth/sessions');
  return data;
}

export async function revokeSession(familyId) {
  await $axiosManager.delete(`/auth/sessions/${familyId}`);
}

export async function revokeOtherSessions() {
  await $axiosManager.delete('/auth/sessions');
}

// MFA -----------------------------------------------------------------------
export async function verifyMfa({ email, session, challengeName, code }) {
  const { data } = await $axiosManager.post('/auth/mfa/verify', {
    email,
    session,
    challengeName,
    code,
  });
  return data;
}

// Recovery code in place of the MFA challenge (lost device). Password is required —
// the code replaces only the second factor.
export async function mfaRecoveryVerify({ email, password, code }) {
  const { data } = await $axiosManager.post('/auth/mfa/recovery-verify', {
    email,
    password,
    code,
  });
  return data;
}

export async function getMfaStatus() {
  const { data } = await $axiosManager.get('/auth/mfa');
  return data;
}

export async function mfaTotpStart(password) {
  const { data } = await $axiosManager.post('/auth/mfa/totp/start', { password });
  return data;
}

export async function mfaTotpVerify({ session, code }) {
  const { data } = await $axiosManager.post('/auth/mfa/totp/verify', { session, code });
  return data;
}

export async function mfaEmailEnable({ password }) {
  const { data } = await $axiosManager.post('/auth/mfa/email/enable', { password });
  return data;
}

// Self-service MFA reset (locked out: lost authenticator + recovery codes). No token.
export async function mfaResetStart({ email }) {
  const { data } = await $axiosManager.post('/auth/mfa/reset/start', { email });
  return data;
}

export async function mfaResetVerify({ email, code }) {
  await $axiosManager.post('/auth/mfa/reset/verify', { email, code });
}

export async function mfaDisable({ password, method }) {
  const { data } = await $axiosManager.post('/auth/mfa/disable', { password, method });
  return data;
}

// Provider linking ----------------------------------------------------------
export async function connectProviderUrl(provider) {
  const { data } = await $axiosManager.get(`/auth/providers/${provider}/connect`);
  return data.url;
}

export async function disconnectProvider(provider) {
  await $axiosManager.delete(`/auth/providers/${provider}`);
}

// sessionLockHeld: the caller fires this POST while already holding AUTH_REFRESH_LOCK_SESSION
// (switch back to the default profile). Every session-refresh path for this request — the
// interceptor's getSessionToken refresh AND the 401 retry's invalidateAndRefresh — would
// re-request that non-reentrant lock from the same tab and deadlock, so __noSessionRefresh
// makes both treat the request as no-refresh/no-retry (a 401 is surfaced to the caller).
export async function switchProfile(
  businessProfileId,
  { recordLastUsed = false, sessionLockHeld = false } = {},
) {
  const { data } = await $axiosManager.post(
    '/auth/switch-profile',
    { businessProfileId, recordLastUsed },
    {
      timeout: AUTH_REQUEST_TIMEOUT_MS,
      ...(sessionLockHeld ? { __noSessionRefresh: true } : {}),
    },
  );
  return data;
}

export async function refreshSession(refreshToken, { forProfile = false } = {}) {
  // __bpTokenRefresh tells the 401 handler this rotates a per-BP app token, not the identity
  // session: a rejected BP refresh token is recoverable by minting via /auth/switch-profile,
  // so it must NOT force-logout the whole session like a rejected session refresh does.
  const { data } = await $axiosManager.post(
    '/auth/refresh',
    { refreshToken },
    { __bpTokenRefresh: forProfile, timeout: AUTH_REQUEST_TIMEOUT_MS },
  );
  return data;
}

export async function changePassword({ oldPassword, newPassword }) {
  await $axiosManager.post('/auth/change-password', { oldPassword, newPassword });
}

// Federated-only account: set a first password (no old password required).
export async function setPassword({ newPassword }) {
  await $axiosManager.post('/auth/set-password', { newPassword });
}

// Federated: ask the backend for the Hosted-UI authorize URL, then redirect.
// `flow` (login | register) lets the backend route a failure back to the right page.
export async function oauthStartUrl(provider, flow) {
  const { data } = await $axiosManager.get(
    `/auth/oauth/${provider}/start`,
    flow ? { params: { flow } } : undefined,
  );
  return data.url;
}

// Exchange the one-time handoff code from the OAuth landing page for tokens. The exchange runs
// in the SPA, so the stored attribution rides the body directly — no OAuth state round-trip.
export async function oauthExchange(code) {
  const { data } = await $axiosManager.post('/auth/oauth/exchange', {
    code,
    attribution: getAttributionForRequest(),
  });
  return data;
}

export const BackendAuthService = {
  register,
  confirm,
  resendCode,
  login,
  forgotPassword,
  resetPassword,
  me,
  updateProfile,
  logout,
  logoutAll,
  listSessions,
  revokeSession,
  revokeOtherSessions,
  verifyMfa,
  mfaRecoveryVerify,
  getMfaStatus,
  mfaTotpStart,
  mfaTotpVerify,
  mfaEmailEnable,
  mfaResetStart,
  mfaResetVerify,
  mfaDisable,
  connectProviderUrl,
  disconnectProvider,
  switchProfile,
  refreshSession,
  changePassword,
  setPassword,
  oauthStartUrl,
  oauthExchange,
};
